import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react"
import { useLocation } from "react-router"
import { Modal, useOpenModal } from "source/shared/components/Modal"
import type { AlertButton, ResolvedAlertData } from "./types"

export const BridgeAlertModalContext = createContext<{
  showWebAlert: (alert: ResolvedAlertData) => void
} | null>(null)

export function BridgeAlertModalProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [alertData, setAlertData] = useState<ResolvedAlertData | null>(null)

  const handleButtonPress = useCallback(
    (buttonIndex: number) => {
      const button = alertData?.buttons[buttonIndex]
      setAlertData(null)
      button?.onPress?.()
    },
    [alertData?.buttons],
  )

  const handleDismiss = useCallback(() => setAlertData(null), [])

  // Ensures the modal goes away when navigating to a different page.
  const { pathname } = useLocation()
  const [lastPathname, setLastPathname] = useState(pathname)
  if (lastPathname !== pathname) {
    setLastPathname(pathname)
    setAlertData(null)
  }

  // Memoized so that opening an alert doesn't re-render all context consumers.
  const contextValue = useMemo(() => ({ showWebAlert: setAlertData }), [])

  return (
    <BridgeAlertModalContext.Provider value={contextValue}>
      {children}
      {alertData && (
        <WebAlertModal
          alert={alertData}
          onButtonPress={handleButtonPress}
          onDismiss={handleDismiss}
        />
      )}
    </BridgeAlertModalContext.Provider>
  )
}

/**
 * Reach focuses this button when the dialog opens. In the event that `Modal.Close`
 * isn't available, (eg. fallback button is used), we would focus on the least destructive option.
 */
function findFocusIndex(buttons: AlertButton[]) {
  if (buttons.some((button) => button.style === "cancel")) return -1

  const firstNonDestructive = buttons.findIndex(
    (button) => button.style !== "destructive",
  )

  return firstNonDestructive === -1 ? 0 : firstNonDestructive
}

function WebAlertModal({
  alert,
  onButtonPress,
  onDismiss,
}: {
  alert: ResolvedAlertData
  onButtonPress: (buttonIndex: number) => void
  onDismiss: () => void
}) {
  const focusRef = useRef<HTMLButtonElement>(null)
  const focusIndex = findFocusIndex(alert.buttons)

  return (
    <Modal onOpenFocusRef={focusIndex === -1 ? undefined : focusRef}>
      <WebAlertModalContent
        alert={alert}
        focusIndex={focusIndex}
        focusRef={focusRef}
        onButtonPress={onButtonPress}
        onDismiss={onDismiss}
      />
    </Modal>
  )
}

// The content is a separate component from WebAlertModal because
// `useOpenModal` reads Modal's context, which only exists below the `<Modal>`.
// Calling it up there would hit the context default and silently never open.
function WebAlertModalContent({
  alert,
  focusIndex,
  focusRef,
  onButtonPress,
  onDismiss,
}: {
  alert: ResolvedAlertData
  focusIndex: number
  focusRef: RefObject<HTMLButtonElement | null>
  onButtonPress: (buttonIndex: number) => void
  onDismiss: () => void
}) {
  const openModal = useOpenModal()

  useEffect(() => {
    openModal()
  }, [openModal])

  return (
    <Modal.Show onClose={onDismiss}>
      <Modal.Header>{alert.title}</Modal.Header>
      <Modal.Body>
        {alert.message && <p>{alert.message}</p>}
        <Modal.Footer className="g-3">
          {alert.buttons.map((button, buttonIndex) => (
            <WebAlertModalButton
              key={buttonIndex}
              button={button}
              focusRef={buttonIndex === focusIndex ? focusRef : undefined}
              onPress={() => onButtonPress(buttonIndex)}
            />
          ))}
        </Modal.Footer>
      </Modal.Body>
    </Modal.Show>
  )
}

function WebAlertModalButton({
  button,
  focusRef,
  onPress,
}: {
  button: AlertButton
  focusRef?: RefObject<HTMLButtonElement | null>
  onPress: () => void
}) {
  if (button.style === "cancel")
    return (
      <Modal.Close className="btn text-btn" onClose={onPress}>
        {button.text}
      </Modal.Close>
    )

  return (
    <button
      className={button.style === "destructive" ? "btn destroy-btn" : "btn"}
      onClick={onPress}
      ref={focusRef}
      type="button"
    >
      {button.text}
    </button>
  )
}
